import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { formatCurrency, formatPercentage, formatNumber } from '../utils/helpers';
import { 
  Plus, Target, TrendingUp, TrendingDown, AlertTriangle, Bell, BellOff,
  BarChart3, LineChart as LineChartIcon, ArrowUp, ArrowDown, Minus, Eye, Trash2,
  Info, CheckCircle, XCircle, Activity, Users, DollarSign, Percent, Clock, Calendar,
  ExternalLink, Link2, Edit
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell,
  LineChart, Line, Area, AreaChart, RadialBarChart, RadialBar, PieChart, Pie
} from 'recharts';

const KPIManagementPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [targets, setTargets] = useState([]);
  const [goals, setGoals] = useState([]);
  const [revenue, setRevenue] = useState([]);
  const [costs, setCosts] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [occupancy, setOccupancy] = useState([]);
  const [locations, setLocations] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedPeriod, setSelectedPeriod] = useState('maand');
  const [alerts, setAlerts] = useState([]);
  const [linkToGoals, setLinkToGoals] = useState(true);
  const [lastSyncTime, setLastSyncTime] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    type: 'omzet',
    target_value: '',
    period: 'maand',
    location_id: '',
    year: new Date().getFullYear(),
    alert_enabled: true,
    alert_threshold: 80
  });

  // Industry benchmarks
  const benchmarks = {
    food_cost: { low: 25, target: 30, high: 35, label: 'Food Cost' },
    personnel_cost: { low: 25, target: 30, high: 35, label: 'Personeelskosten' },
    avg_spending: { low: 20, target: 35, high: 50, label: 'Gem. Besteding' },
    occupancy: { low: 60, target: 75, high: 90, label: 'Bezettingsgraad' },
    profit_margin: { low: 5, target: 10, high: 15, label: 'Winstmarge' },
    no_show: { low: 2, target: 5, high: 10, label: 'No-show %' }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const currentYear = new Date().getFullYear();
      const startDate = new Date(currentYear, 0, 1).toISOString().split('T')[0];
      const endDate = new Date().toISOString().split('T')[0];

      const [targetsRes, revRes, costRes, locRes, resRes, occRes, goalsRes] = await Promise.all([
        api.get('/kpi-targets', { params: { year: currentYear } }),
        api.get('/revenue', { params: { start_date: startDate, end_date: endDate } }),
        api.get('/costs', { params: { start_date: startDate, end_date: endDate } }),
        api.get('/locations'),
        api.get('/reservations', { params: { start_date: startDate, end_date: endDate } }),
        api.get('/occupancy', { params: { start_date: startDate, end_date: endDate } }),
        api.get('/goals')
      ]);
      setTargets(targetsRes.data);
      setRevenue(revRes.data);
      setCosts(costRes.data);
      setLocations(locRes.data);
      setReservations(resRes.data);
      setOccupancy(occRes.data);
      setGoals(goalsRes.data);
      
      // Sync KPI goals with actual values (silent, in background)
      try {
        const syncRes = await api.post('/kpi-targets/sync-goals');
        // Refresh goals after sync
        const updatedGoalsRes = await api.get('/goals');
        setGoals(updatedGoalsRes.data);
        setLastSyncTime(new Date());
        if (syncRes.data?.updated_count > 0) {
          console.log(`${syncRes.data.updated_count} goals gesynchroniseerd`);
        }
      } catch (syncError) {
        console.log('KPI sync skipped:', syncError);
      }
      
      // Generate alerts based on KPI performance
      generateAlerts(targetsRes.data, revRes.data, costRes.data, resRes.data, occRes.data);
    } catch (error) {
      toast.error('Fout bij laden data');
    } finally {
      setLoading(false);
    }
  };

  const generateAlerts = (targets, revenue, costs, reservations, occupancy) => {
    const newAlerts = [];
    const totalRevenue = revenue.reduce((sum, r) => sum + (r.amount || 0), 0);
    const totalFoodCost = costs.reduce((sum, c) => sum + (c.food_cost || 0), 0);
    const foodCostPct = totalRevenue > 0 ? (totalFoodCost / totalRevenue) * 100 : 0;
    const noShowCount = reservations.filter(r => r.status === 'no-show').length;
    const noShowPct = reservations.length > 0 ? (noShowCount / reservations.length) * 100 : 0;
    
    // Check omzet target
    const omzetTarget = targets.find(t => t.type === 'omzet');
    if (omzetTarget && totalRevenue < omzetTarget.target_value * 0.8) {
      newAlerts.push({
        type: 'warning',
        kpi: 'Omzet',
        message: `Omzet ligt ${formatPercentage(((omzetTarget.target_value - totalRevenue) / omzetTarget.target_value) * 100)} onder doel`,
        value: totalRevenue,
        target: omzetTarget.target_value
      });
    }
    
    // Check food cost
    if (foodCostPct > 35) {
      newAlerts.push({
        type: 'danger',
        kpi: 'Food Cost',
        message: `Food cost is ${formatPercentage(foodCostPct)} - boven benchmark van 35%`,
        value: foodCostPct,
        target: 35
      });
    }
    
    // Check no-show
    if (noShowPct > 10) {
      newAlerts.push({
        type: 'warning',
        kpi: 'No-show',
        message: `No-show percentage is ${formatPercentage(noShowPct)} - boven acceptabel niveau`,
        value: noShowPct,
        target: 10
      });
    }
    
    setAlerts(newAlerts);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Create KPI target
      const kpiResponse = await api.post('/kpi-targets', {
        ...formData,
        target_value: parseFloat(formData.target_value) || 0,
        location_id: formData.location_id || null,
        alert_threshold: parseInt(formData.alert_threshold) || 80
      });
      
      // Also create a linked goal in Doelen & Structuur if enabled
      if (linkToGoals) {
        const periodMap = {
          'dag': 'dag',
          'week': 'week',
          'maand': 'maand',
          'kwartaal': 'kwartaal',
          'jaar': 'jaar'
        };
        
        await api.post('/goals', {
          title: `KPI: ${formData.name}`,
          description: `KPI doel: ${typeLabels[formData.type] || formData.type} - Doelwaarde: ${formData.target_value}`,
          period: periodMap[formData.period] || 'maand',
          status: 'actief',
          progress: 0,
          kpi_target_id: kpiResponse.data?.id || null,
          category: 'kpi'
        });
        toast.success('KPI doel toegevoegd en gekoppeld aan Doelen & Structuur');
      } else {
        toast.success('KPI doel toegevoegd');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('KPI doel verwijderen?')) return;
    try {
      await api.delete(`/kpi-targets/${id}`);
      toast.success('KPI doel verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  // Find linked goal for a KPI target
  const findLinkedGoal = (target) => {
    return goals.find(g => 
      g.kpi_target_id === target.id || 
      g.title?.includes(target.name) ||
      g.title?.toLowerCase().includes('kpi:')
    );
  };

  // Open target detail dialog
  const openTargetDetail = (target) => {
    setSelectedTarget(target);
    setDetailDialogOpen(true);
  };

  // Navigate to linked goal
  const navigateToGoal = (goalId) => {
    navigate('/company/doelen-structuur');
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'omzet',
      target_value: '',
      period: 'maand',
      location_id: '',
      year: new Date().getFullYear(),
      alert_enabled: true,
      alert_threshold: 80
    });
  };

  // Calculate actuals
  const totalRevenue = revenue.reduce((sum, r) => sum + (r.amount || 0), 0);
  const totalFoodCost = costs.reduce((sum, c) => sum + (c.food_cost || 0), 0);
  const totalBeverageCost = costs.reduce((sum, c) => sum + (c.beverage_cost || 0), 0);
  const totalPersonnelCost = costs.reduce((sum, c) => sum + (c.personnel_cost || 0), 0);
  const totalOtherCost = costs.reduce((sum, c) => sum + (c.other_cost || 0), 0);
  const totalCosts = totalFoodCost + totalBeverageCost + totalPersonnelCost + totalOtherCost;
  const totalGuests = revenue.reduce((sum, r) => sum + (r.guests || 0), 0);
  const avgSpending = totalGuests > 0 ? totalRevenue / totalGuests : 0;
  const foodCostPercentage = totalRevenue > 0 ? (totalFoodCost / totalRevenue) * 100 : 0;
  const personnelCostPercentage = totalRevenue > 0 ? (totalPersonnelCost / totalRevenue) * 100 : 0;
  const profitMargin = totalRevenue > 0 ? ((totalRevenue - totalCosts) / totalRevenue) * 100 : 0;
  const avgOccupancy = occupancy.length > 0 
    ? occupancy.reduce((sum, o) => sum + (o.capacity > 0 ? (o.actual_guests / o.capacity) * 100 : 0), 0) / occupancy.length 
    : 0;
  const noShowCount = reservations.filter(r => r.status === 'no-show').length;
  const noShowPercentage = reservations.length > 0 ? (noShowCount / reservations.length) * 100 : 0;
  const revenuePerHour = totalRevenue / (revenue.length * 8 || 1); // Assume 8 hours per day

  // Get status color
  const getStatus = (actual, target, type, inverted = false) => {
    if (!target || target === 0) return { color: 'gray', label: 'Geen doel', progress: 0 };
    
    const ratio = actual / target;
    const progress = Math.min(ratio * 100, 150);
    const shouldInvert = inverted || ['food_cost', 'personnel_cost', 'no_show'].includes(type);
    
    if (shouldInvert) {
      if (ratio <= 0.9) return { color: 'green', label: 'Uitstekend', progress };
      if (ratio <= 1.1) return { color: 'orange', label: 'Let op', progress };
      return { color: 'red', label: 'Actie vereist', progress };
    } else {
      if (ratio >= 1) return { color: 'green', label: 'Behaald', progress };
      if (ratio >= 0.8) return { color: 'orange', label: 'Bijna', progress };
      return { color: 'red', label: 'Achterstand', progress };
    }
  };

  // Extended KPI data
  const kpiData = [
    { name: 'Omzet', type: 'omzet', actual: totalRevenue, target: targets.find(t => t.type === 'omzet')?.target_value || 0, format: 'currency', icon: DollarSign, description: 'Totale omzet dit jaar' },
    { name: 'Food Cost', type: 'food_cost', actual: foodCostPercentage, target: targets.find(t => t.type === 'food_cost')?.target_value || benchmarks.food_cost.target, format: 'percentage', icon: Percent, inverted: true, description: 'Inkoopkosten als % van omzet' },
    { name: 'Personeelskosten', type: 'personnel_cost', actual: personnelCostPercentage, target: targets.find(t => t.type === 'personnel_cost')?.target_value || benchmarks.personnel_cost.target, format: 'percentage', icon: Users, inverted: true, description: 'Loonkosten als % van omzet' },
    { name: 'Gem. Besteding', type: 'avg_spending', actual: avgSpending, target: targets.find(t => t.type === 'avg_spending')?.target_value || benchmarks.avg_spending.target, format: 'currency', icon: TrendingUp, description: 'Gemiddelde uitgave per gast' },
    { name: 'Bezettingsgraad', type: 'occupancy', actual: avgOccupancy, target: targets.find(t => t.type === 'occupancy')?.target_value || benchmarks.occupancy.target, format: 'percentage', icon: Activity, description: 'Gemiddelde stoelbezetting' },
    { name: 'Winstmarge', type: 'profit_margin', actual: profitMargin, target: targets.find(t => t.type === 'profit_margin')?.target_value || benchmarks.profit_margin.target, format: 'percentage', icon: Target, description: 'Netto winst als % van omzet' },
    { name: 'No-show %', type: 'no_show', actual: noShowPercentage, target: targets.find(t => t.type === 'no_show')?.target_value || benchmarks.no_show.target, format: 'percentage', icon: XCircle, inverted: true, description: 'Reserveringen die niet komen opdagen' },
    { name: 'Omzet per uur', type: 'revenue_per_hour', actual: revenuePerHour, target: targets.find(t => t.type === 'revenue_per_hour')?.target_value || 0, format: 'currency', icon: Clock, description: 'Gemiddelde omzet per operationeel uur' }
  ];

  // Monthly trend data
  const months = ['Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
  const trendData = months.slice(0, new Date().getMonth() + 1).map((month, idx) => {
    const monthRevenue = revenue.filter(r => new Date(r.date).getMonth() === idx);
    const monthCosts = costs.filter(c => new Date(c.date).getMonth() === idx);
    const monthOmzet = monthRevenue.reduce((sum, r) => sum + (r.amount || 0), 0);
    const monthFoodCost = monthCosts.reduce((sum, c) => sum + (c.food_cost || 0), 0);
    const monthGuests = monthRevenue.reduce((sum, r) => sum + (r.guests || 0), 0);
    
    return {
      month,
      omzet: monthOmzet,
      foodCost: monthOmzet > 0 ? (monthFoodCost / monthOmzet) * 100 : 0,
      gasten: monthGuests,
      avgBesteding: monthGuests > 0 ? monthOmzet / monthGuests : 0
    };
  });

  const statusColors = { green: '#22C55E', orange: '#F59E0B', red: '#EF4444', gray: '#9CA3AF' };

  const typeLabels = {
    omzet: 'Omzet (€)',
    food_cost: 'Food Cost (%)',
    personnel_cost: 'Personeelskosten (%)',
    avg_spending: 'Gem. Besteding (€)',
    occupancy: 'Bezettingsgraad (%)',
    profit_margin: 'Winstmarge (%)',
    no_show: 'No-show (%)',
    revenue_per_hour: 'Omzet per uur (€)'
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#C2410C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6 animate-fade-in" data-testid="kpi-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1C1917]" style={{ fontFamily: 'Manrope' }}>
            KPI Management
          </h1>
          <p className="text-[#57534E] mt-1">
            Monitor prestaties, stel doelen en vergelijk met benchmarks
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            className="text-emerald-600 border-emerald-300"
            onClick={async () => {
              setSyncing(true);
              try {
                const syncRes = await api.post('/kpi-targets/sync-goals');
                const updatedGoalsRes = await api.get('/goals');
                setGoals(updatedGoalsRes.data);
                setLastSyncTime(new Date());
                toast.success(`${syncRes.data?.updated_count || 0} doelen gesynchroniseerd`);
              } catch (error) {
                toast.error('Synchronisatie mislukt');
              } finally {
                setSyncing(false);
              }
            }}
            disabled={syncing}
          >
            {syncing ? (
              <div className="w-4 h-4 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin mr-2" />
            ) : (
              <Activity className="w-4 h-4 mr-2" />
            )}
            Sync Doelen
          </Button>
          {alerts.length > 0 && (
            <Button 
              variant="outline" 
              className="text-amber-600 border-amber-300 bg-amber-50"
              onClick={() => setAlertDialogOpen(true)}
            >
              <Bell className="w-4 h-4 mr-2" />
              {alerts.length} Alert{alerts.length > 1 ? 's' : ''}
            </Button>
          )}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-kpi-btn">
                <Plus className="w-4 h-4 mr-2" />
                KPI Doel
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Nieuw KPI Doel</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Naam</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="bijv. Maandelijkse omzet doel"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>KPI Type</Label>
                    <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="omzet">Omzet (€)</SelectItem>
                        <SelectItem value="food_cost">Food Cost (%)</SelectItem>
                        <SelectItem value="personnel_cost">Personeelskosten (%)</SelectItem>
                        <SelectItem value="avg_spending">Gem. Besteding (€)</SelectItem>
                        <SelectItem value="occupancy">Bezettingsgraad (%)</SelectItem>
                        <SelectItem value="profit_margin">Winstmarge (%)</SelectItem>
                        <SelectItem value="no_show">No-show (%)</SelectItem>
                        <SelectItem value="revenue_per_hour">Omzet per uur (€)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Periode</Label>
                    <Select value={formData.period} onValueChange={(value) => setFormData({ ...formData, period: value })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dag">Per dag</SelectItem>
                        <SelectItem value="week">Per week</SelectItem>
                        <SelectItem value="maand">Per maand</SelectItem>
                        <SelectItem value="kwartaal">Per kwartaal</SelectItem>
                        <SelectItem value="jaar">Per jaar</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Doelwaarde</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.target_value}
                      onChange={(e) => setFormData({ ...formData, target_value: e.target.value })}
                      placeholder={formData.type.includes('cost') || ['occupancy', 'profit_margin', 'no_show'].includes(formData.type) ? '25' : '10000'}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Jaar</Label>
                    <Input
                      type="number"
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                      required
                    />
                  </div>
                </div>
                <div className="p-4 bg-stone-50 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="cursor-pointer">Alert bij afwijking</Label>
                    <Switch
                      checked={formData.alert_enabled}
                      onCheckedChange={(checked) => setFormData({ ...formData, alert_enabled: checked })}
                    />
                  </div>
                  {formData.alert_enabled && (
                    <div className="space-y-2">
                      <Label className="text-sm text-[#57534E]">Alert onder {formData.alert_threshold}% van doel</Label>
                      <Input
                        type="number"
                        value={formData.alert_threshold}
                        onChange={(e) => setFormData({ ...formData, alert_threshold: e.target.value })}
                        min="0"
                        max="100"
                      />
                    </div>
                  )}
                </div>
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Link2 className="w-4 h-4 text-emerald-600" />
                      <Label className="cursor-pointer text-emerald-800">Koppelen aan Doelen & Structuur</Label>
                    </div>
                    <Switch
                      checked={linkToGoals}
                      onCheckedChange={setLinkToGoals}
                    />
                  </div>
                  <p className="text-xs text-emerald-700">
                    Maakt automatisch een gekoppeld doel aan in de Doelen & Structuur module
                  </p>
                </div>
                <Button type="submit" className="w-full btn-primary">Toevoegen</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Alerts Banner */}
      {alerts.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <div className="flex-1">
              <p className="font-medium text-amber-800">
                {alerts.length} KPI{alerts.length > 1 ? "'s hebben" : " heeft"} aandacht nodig
              </p>
              <p className="text-sm text-amber-700">
                {alerts.map(a => a.kpi).join(', ')}
              </p>
            </div>
            <Button variant="outline" size="sm" className="text-amber-700" onClick={() => setAlertDialogOpen(true)}>
              Bekijk details
            </Button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overzicht</TabsTrigger>
          <TabsTrigger value="trends">Trends</TabsTrigger>
          <TabsTrigger value="benchmarks">Benchmarks</TabsTrigger>
          <TabsTrigger value="targets">Doelen</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6 space-y-6">
          {/* KPI Status Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {kpiData.slice(0, 8).map(kpi => {
              const status = getStatus(kpi.actual, kpi.target, kpi.type, kpi.inverted);
              const Icon = kpi.icon;
              return (
                <Card key={kpi.type} className="card-base relative overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="absolute top-0 left-0 w-1 h-full" style={{ backgroundColor: statusColors[status.color] }} />
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: statusColors[status.color] + '20' }}>
                        <Icon className="w-5 h-5" style={{ color: statusColors[status.color] }} />
                      </div>
                      <div className={`traffic-light traffic-${status.color}`} />
                    </div>
                    <p className="text-sm font-medium text-[#57534E] uppercase tracking-wide">{kpi.name}</p>
                    <p className="text-2xl font-bold mt-1" style={{ fontFamily: 'JetBrains Mono' }}>
                      {kpi.format === 'currency' ? formatCurrency(kpi.actual) : formatPercentage(kpi.actual)}
                    </p>
                    {kpi.target > 0 && (
                      <>
                        <div className="mt-2 flex items-center gap-2">
                          <Progress value={Math.min((kpi.actual / kpi.target) * 100, 100)} className="h-1.5 flex-1" />
                          <span className="text-xs text-[#57534E]">
                            {Math.round((kpi.actual / kpi.target) * 100)}%
                          </span>
                        </div>
                        <p className="text-xs text-[#78716C] mt-1">
                          Doel: {kpi.format === 'currency' ? formatCurrency(kpi.target) : formatPercentage(kpi.target)}
                        </p>
                      </>
                    )}
                    <Badge className="mt-2" style={{ backgroundColor: statusColors[status.color] + '20', color: statusColors[status.color] }}>
                      {status.label}
                    </Badge>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* KPI Comparison Chart */}
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#C2410C]" />
                  Actueel vs Doel
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      data={kpiData.filter(k => k.target > 0).map(kpi => ({
                        name: kpi.name.length > 10 ? kpi.name.substring(0, 10) + '...' : kpi.name,
                        actueel: kpi.format === 'percentage' ? kpi.actual : kpi.actual / 1000,
                        doel: kpi.format === 'percentage' ? kpi.target : kpi.target / 1000,
                        status: getStatus(kpi.actual, kpi.target, kpi.type, kpi.inverted).color
                      }))} 
                      layout="vertical"
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                      <XAxis type="number" tick={{ fontSize: 11, fill: '#57534E' }} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#57534E' }} width={80} />
                      <Tooltip formatter={(value, name) => [value.toFixed(1), name === 'actueel' ? 'Actueel' : 'Doel']} />
                      <Legend />
                      <Bar dataKey="doel" fill="#E7E5E4" name="Doel" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="actueel" name="Actueel" radius={[0, 4, 4, 0]}>
                        {kpiData.filter(k => k.target > 0).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={statusColors[getStatus(entry.actual, entry.target, entry.type, entry.inverted).color]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Status Distribution */}
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-[#C2410C]" />
                  KPI Status Verdeling
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px] flex items-center justify-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Behaald', value: kpiData.filter(k => k.target > 0 && getStatus(k.actual, k.target, k.type, k.inverted).color === 'green').length, fill: '#22C55E' },
                          { name: 'Let op', value: kpiData.filter(k => k.target > 0 && getStatus(k.actual, k.target, k.type, k.inverted).color === 'orange').length, fill: '#F59E0B' },
                          { name: 'Achterstand', value: kpiData.filter(k => k.target > 0 && getStatus(k.actual, k.target, k.type, k.inverted).color === 'red').length, fill: '#EF4444' },
                          { name: 'Geen doel', value: kpiData.filter(k => !k.target || k.target === 0).length, fill: '#9CA3AF' }
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}
                      />
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center gap-4 mt-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-500" />
                    <span className="text-sm">Behaald</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-amber-500" />
                    <span className="text-sm">Let op</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <span className="text-sm">Achterstand</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Trends Tab */}
        <TabsContent value="trends" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Trend */}
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <LineChartIcon className="w-5 h-5 text-[#C2410C]" />
                  Omzet Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#57534E' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#57534E' }} tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} />
                      <Tooltip formatter={(value) => [formatCurrency(value), 'Omzet']} />
                      <Area type="monotone" dataKey="omzet" stroke="#C2410C" fill="#C2410C20" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Food Cost Trend */}
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingDown className="w-5 h-5 text-[#C2410C]" />
                  Food Cost Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#57534E' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#57534E' }} domain={[0, 50]} tickFormatter={(v) => `${v}%`} />
                      <Tooltip formatter={(value) => [formatPercentage(value), 'Food Cost']} />
                      <Line type="monotone" dataKey="foodCost" stroke="#EF4444" strokeWidth={2} dot={{ fill: '#EF4444' }} />
                      {/* Benchmark line */}
                      <Line type="monotone" dataKey={() => benchmarks.food_cost.target} stroke="#9CA3AF" strokeDasharray="5 5" name="Benchmark" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Guest Count Trend */}
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#C2410C]" />
                  Aantal Gasten per Maand
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#57534E' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#57534E' }} />
                      <Tooltip formatter={(value) => [formatNumber(value), 'Gasten']} />
                      <Bar dataKey="gasten" fill="#C2410C" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Average Spending Trend */}
            <Card className="card-base">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-[#C2410C]" />
                  Gem. Besteding per Maand
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#57534E' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#57534E' }} tickFormatter={(v) => `€${v}`} />
                      <Tooltip formatter={(value) => [formatCurrency(value), 'Gem. Besteding']} />
                      <Line type="monotone" dataKey="avgBesteding" stroke="#22C55E" strokeWidth={2} dot={{ fill: '#22C55E' }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Benchmarks Tab */}
        <TabsContent value="benchmarks" className="mt-6 space-y-6">
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-[#C2410C]" />
                Industrie Benchmarks Vergelijking
              </CardTitle>
              <p className="text-sm text-[#57534E] mt-1">Vergelijk uw prestaties met gemiddelde horeca benchmarks</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {Object.entries(benchmarks).map(([key, benchmark]) => {
                  const kpi = kpiData.find(k => k.type === key);
                  const actual = kpi?.actual || 0;
                  const isGood = key === 'no_show' 
                    ? actual <= benchmark.target 
                    : ['food_cost', 'personnel_cost'].includes(key) 
                      ? actual <= benchmark.target 
                      : actual >= benchmark.target;
                  
                  return (
                    <div key={key} className="p-4 bg-stone-50 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-[#1C1917]">{benchmark.label}</h4>
                          <p className="text-sm text-[#57534E]">
                            Benchmark: {['food_cost', 'personnel_cost', 'occupancy', 'profit_margin', 'no_show'].includes(key) ? `${benchmark.target}%` : `€${benchmark.target}`}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold" style={{ color: isGood ? '#22C55E' : '#EF4444' }}>
                            {kpi?.format === 'percentage' ? formatPercentage(actual) : formatCurrency(actual)}
                          </p>
                          <Badge className={isGood ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                            {isGood ? (
                              <><CheckCircle className="w-3 h-3 mr-1" /> Binnen benchmark</>
                            ) : (
                              <><XCircle className="w-3 h-3 mr-1" /> Buiten benchmark</>
                            )}
                          </Badge>
                        </div>
                      </div>
                      <div className="relative h-4 bg-stone-200 rounded-full overflow-hidden">
                        {/* Low zone */}
                        <div 
                          className="absolute h-full bg-emerald-200" 
                          style={{ width: `${(benchmark.low / benchmark.high) * 100}%` }}
                        />
                        {/* Target zone */}
                        <div 
                          className="absolute h-full bg-emerald-400" 
                          style={{ left: `${(benchmark.low / benchmark.high) * 100}%`, width: `${((benchmark.target - benchmark.low) / benchmark.high) * 100}%` }}
                        />
                        {/* High zone */}
                        <div 
                          className="absolute h-full bg-amber-400" 
                          style={{ left: `${(benchmark.target / benchmark.high) * 100}%`, width: `${((benchmark.high - benchmark.target) / benchmark.high) * 100}%` }}
                        />
                        {/* Actual marker */}
                        <div 
                          className="absolute h-6 w-1 bg-[#1C1917] rounded -top-1"
                          style={{ left: `${Math.min((actual / benchmark.high) * 100, 100)}%` }}
                        />
                      </div>
                      <div className="flex justify-between mt-2 text-xs text-[#78716C]">
                        <span>Laag: {benchmark.low}{['food_cost', 'personnel_cost', 'occupancy', 'profit_margin', 'no_show'].includes(key) ? '%' : '€'}</span>
                        <span>Target: {benchmark.target}{['food_cost', 'personnel_cost', 'occupancy', 'profit_margin', 'no_show'].includes(key) ? '%' : '€'}</span>
                        <span>Hoog: {benchmark.high}{['food_cost', 'personnel_cost', 'occupancy', 'profit_margin', 'no_show'].includes(key) ? '%' : '€'}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Targets Tab */}
        <TabsContent value="targets" className="mt-6">
          <Card className="card-base">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Alle KPI Doelen</CardTitle>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/company/doelen-structuur')}
                className="text-[#C2410C]"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Naar Doelen & Structuur
              </Button>
            </CardHeader>
            <CardContent>
              {targets.length === 0 ? (
                <div className="text-center py-12">
                  <Target className="w-12 h-12 mx-auto text-[#57534E]/30 mb-4" />
                  <p className="text-[#57534E]">Nog geen KPI doelen ingesteld</p>
                  <Button className="mt-4 btn-primary" onClick={() => setDialogOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" /> Eerste doel toevoegen
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Naam</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Doelwaarde</TableHead>
                      <TableHead>Periode</TableHead>
                      <TableHead>Actueel</TableHead>
                      <TableHead>Voortgang</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Gekoppeld</TableHead>
                      <TableHead>Alert</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {targets.map(target => {
                      const kpi = kpiData.find(k => k.type === target.type);
                      const status = kpi ? getStatus(kpi.actual, target.target_value, target.type, kpi.inverted) : { color: 'gray', label: 'N/A' };
                      const linkedGoal = findLinkedGoal(target);
                      const progress = kpi && target.target_value > 0 ? Math.min((kpi.actual / target.target_value) * 100, 150) : 0;
                      
                      return (
                        <TableRow 
                          key={target.id} 
                          className="cursor-pointer hover:bg-stone-50 transition-colors"
                          onClick={() => openTargetDetail(target)}
                        >
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <Eye className="w-4 h-4 text-[#C2410C]" />
                              {target.name}
                            </div>
                          </TableCell>
                          <TableCell>{typeLabels[target.type] || target.type}</TableCell>
                          <TableCell className="font-mono">
                            {['food_cost', 'personnel_cost', 'occupancy', 'profit_margin', 'no_show'].includes(target.type) 
                              ? formatPercentage(target.target_value)
                              : formatCurrency(target.target_value)
                            }
                          </TableCell>
                          <TableCell className="capitalize">{target.period}</TableCell>
                          <TableCell className="font-mono">
                            {kpi ? (kpi.format === 'percentage' ? formatPercentage(kpi.actual) : formatCurrency(kpi.actual)) : '-'}
                          </TableCell>
                          <TableCell>
                            <div className="w-20">
                              <Progress value={Math.min(progress, 100)} className="h-1.5" />
                              <span className="text-xs text-[#57534E]">{Math.round(progress)}%</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge style={{ backgroundColor: statusColors[status.color] + '20', color: statusColors[status.color] }}>
                              {status.label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {linkedGoal ? (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="text-emerald-600 p-1 h-auto"
                                onClick={(e) => { e.stopPropagation(); navigateToGoal(linkedGoal.id); }}
                              >
                                <Link2 className="w-4 h-4" />
                              </Button>
                            ) : (
                              <span className="text-stone-400 text-xs">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {target.alert_enabled ? (
                              <Bell className="w-4 h-4 text-amber-500" />
                            ) : (
                              <BellOff className="w-4 h-4 text-stone-400" />
                            )}
                          </TableCell>
                          <TableCell>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-rose-600" 
                              onClick={(e) => { e.stopPropagation(); handleDelete(target.id); }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Target Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-[#C2410C]" />
              KPI Doel Details
            </DialogTitle>
          </DialogHeader>
          {selectedTarget && (() => {
            const kpi = kpiData.find(k => k.type === selectedTarget.type);
            const status = kpi ? getStatus(kpi.actual, selectedTarget.target_value, selectedTarget.type, kpi.inverted) : { color: 'gray', label: 'N/A' };
            const linkedGoal = findLinkedGoal(selectedTarget);
            const progress = kpi && selectedTarget.target_value > 0 ? Math.min((kpi.actual / selectedTarget.target_value) * 100, 150) : 0;
            
            return (
              <div className="space-y-6 pt-4">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-[#1C1917]">{selectedTarget.name}</h3>
                    <p className="text-sm text-[#57534E]">{typeLabels[selectedTarget.type] || selectedTarget.type}</p>
                  </div>
                  <Badge 
                    className="text-sm"
                    style={{ backgroundColor: statusColors[status.color] + '20', color: statusColors[status.color] }}
                  >
                    {status.label}
                  </Badge>
                </div>

                {/* Progress Card */}
                <div className="p-4 bg-stone-50 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium">Voortgang</span>
                    <span className="text-lg font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                      {Math.round(progress)}%
                    </span>
                  </div>
                  <Progress value={Math.min(progress, 100)} className="h-2 mb-3" />
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-[#57534E]">Actueel</p>
                      <p className="font-bold text-lg" style={{ fontFamily: 'JetBrains Mono' }}>
                        {kpi ? (kpi.format === 'percentage' ? formatPercentage(kpi.actual) : formatCurrency(kpi.actual)) : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[#57534E]">Doel</p>
                      <p className="font-bold text-lg" style={{ fontFamily: 'JetBrains Mono' }}>
                        {['food_cost', 'personnel_cost', 'occupancy', 'profit_margin', 'no_show'].includes(selectedTarget.type) 
                          ? formatPercentage(selectedTarget.target_value)
                          : formatCurrency(selectedTarget.target_value)
                        }
                      </p>
                    </div>
                  </div>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-stone-50 rounded-lg">
                    <p className="text-xs text-[#57534E] mb-1">Periode</p>
                    <p className="font-medium capitalize">{selectedTarget.period}</p>
                  </div>
                  <div className="p-3 bg-stone-50 rounded-lg">
                    <p className="text-xs text-[#57534E] mb-1">Jaar</p>
                    <p className="font-medium">{selectedTarget.year}</p>
                  </div>
                  <div className="p-3 bg-stone-50 rounded-lg">
                    <p className="text-xs text-[#57534E] mb-1">Alert</p>
                    <div className="flex items-center gap-2">
                      {selectedTarget.alert_enabled ? (
                        <>
                          <Bell className="w-4 h-4 text-amber-500" />
                          <span className="font-medium">Aan (onder {selectedTarget.alert_threshold}%)</span>
                        </>
                      ) : (
                        <>
                          <BellOff className="w-4 h-4 text-stone-400" />
                          <span className="font-medium text-stone-400">Uit</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="p-3 bg-stone-50 rounded-lg">
                    <p className="text-xs text-[#57534E] mb-1">Gekoppeld doel</p>
                    {linkedGoal ? (
                      <Button 
                        variant="link" 
                        className="p-0 h-auto text-emerald-600"
                        onClick={() => { setDetailDialogOpen(false); navigateToGoal(linkedGoal.id); }}
                      >
                        <Link2 className="w-4 h-4 mr-1" />
                        {linkedGoal.title?.substring(0, 20)}...
                      </Button>
                    ) : (
                      <span className="text-stone-400">Niet gekoppeld</span>
                    )}
                  </div>
                </div>

                {/* Benchmark Info (if applicable) */}
                {benchmarks[selectedTarget.type] && (
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Info className="w-4 h-4 text-blue-600" />
                      <span className="font-medium text-blue-800">Industrie Benchmark</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div>
                        <p className="text-blue-600">Laag</p>
                        <p className="font-medium">{benchmarks[selectedTarget.type].low}%</p>
                      </div>
                      <div>
                        <p className="text-blue-600">Target</p>
                        <p className="font-medium">{benchmarks[selectedTarget.type].target}%</p>
                      </div>
                      <div>
                        <p className="text-blue-600">Hoog</p>
                        <p className="font-medium">{benchmarks[selectedTarget.type].high}%</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  {linkedGoal ? (
                    <Button 
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => { setDetailDialogOpen(false); navigateToGoal(linkedGoal.id); }}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Bekijk in Doelen & Structuur
                    </Button>
                  ) : (
                    <Button 
                      className="flex-1"
                      variant="outline"
                      onClick={() => { setDetailDialogOpen(false); navigate('/company/doelen-structuur'); }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Koppel aan Doel
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    className="text-rose-600 border-rose-300"
                    onClick={() => { handleDelete(selectedTarget.id); setDetailDialogOpen(false); }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Alerts Dialog */}
      <Dialog open={alertDialogOpen} onOpenChange={setAlertDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              KPI Alerts
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {alerts.length === 0 ? (
              <p className="text-center py-8 text-[#57534E]">Geen actieve alerts</p>
            ) : (
              alerts.map((alert, idx) => (
                <div 
                  key={idx} 
                  className={`p-4 rounded-lg border-l-4 ${
                    alert.type === 'danger' ? 'bg-red-50 border-red-500' : 'bg-amber-50 border-amber-500'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {alert.type === 'danger' ? (
                      <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div>
                      <h4 className={`font-semibold ${alert.type === 'danger' ? 'text-red-800' : 'text-amber-800'}`}>
                        {alert.kpi}
                      </h4>
                      <p className={`text-sm ${alert.type === 'danger' ? 'text-red-700' : 'text-amber-700'}`}>
                        {alert.message}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
            <Button variant="outline" className="w-full" onClick={() => setAlertDialogOpen(false)}>
              Sluiten
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KPIManagementPage;
